import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { SelectGroupOption } from './select-group';
import Icon, { ICONS, COLORS } from './icon';

export const DROP_DOWN_POSITIONS = {
	LEFT: 'drop-left',
	RIGHT: 'drop-right',
	DOWN_LEFT: 'drop-down-left',
	DOWN_RIGHT: 'drop-down-right',
};

export interface SelectGroupProps {
	options: SelectGroupOption[],
	onChange: (event: any) => any;
	label: string;
	selected: SelectGroupOption;
	invalid?: boolean;
	disabled?: boolean;
	dropdown?: boolean;
	position?: string;
	dropdownPosition?: string;
	optionHeight?: number;
	showLabel?: boolean;
	openAtSelected?: boolean;
	className?: string;
	forceOpenUp?: boolean; // force menu to open upward
	isOpen?: boolean;
	onBlur?: Function;
	onFocus?: Function;
	hasCarrot?: boolean;
	cover?: boolean; // the list will cover the currently selected option when open
	enclosed?: boolean; // is the entire select, enclosed?
}

const getShiftedCSS = (
	optionHeight: number,
	forceOpenUp: boolean,
	options: SelectGroupOption[],
	rootNode: HTMLElement | null,
) => {
	// Calculate dropdown height based on number of options (max 6 visible)
	const top = optionHeight * Math.min(options?.length ?? 0, 6);

	if (forceOpenUp) {
		return { top: `-${top + 4}px` };
	} else if (rootNode) {
		//+4 to space out the node to where the dropdown will start
		return { top: `${rootNode.clientHeight + 4}px` };
	}

	return {};
};

export default function SelectSmall(props: SelectGroupProps) {
	const {
		options,
		onChange,
		label,
		selected,
		invalid,
		disabled,
		dropdown,
		position,
		dropdownPosition,
		optionHeight = 36,
		showLabel = false,
		openAtSelected = true,
		className,
		forceOpenUp = false,
		isOpen = false,
		onBlur = () => {},
		onFocus = () => { },
		hasCarrot = false,
		enclosed = false
	} = props;

	const selectRef = useRef<HTMLSpanElement>(null);

	const [open, setOpen] = useState(false);
	const [activeClass, setActiveClass] = useState('closed');
	const [selectedIndex, setSelectedIndex] = useState(
		options.findIndex((o) => o.value === selected.value),
	);
	const [shiftedBy, setShiftedBy] = useState(
		getShiftedCSS(optionHeight, forceOpenUp, options, selectRef.current),
	);

	const minWidth = useMemo(() => {
		let longest = 0;
		options.forEach((op: SelectGroupOption) => {
			// find longest word
			longest = op.label.length > longest ? op.label.length : longest;
		});

		let calcedWidth = longest * 18; // 18px per letter?
		if (calcedWidth > 190) {
			calcedWidth = 190; // max min width
		} else if (calcedWidth < 100) {
			calcedWidth = 72; // min min width
		}
		return calcedWidth;
	}, [options]);

	function animationFinished() {
		if (open && activeClass === 'closed') {
			setOpen(false);
			setShiftedBy(getShiftedCSS(
				optionHeight,
				forceOpenUp,
				options,

				selectRef.current,
			));
		}
	}

	function close() {
		onBlur();
		setActiveClass('closed');
	}

	function doOpen() {
		onFocus();
		
		// Check if we should open upward when there's not enough space
		let shouldOpenUp = forceOpenUp;
		if (!forceOpenUp && selectRef.current) {
			const rect = selectRef.current.getBoundingClientRect();
			const dropdownHeight = Math.min(options.length, 6) * optionHeight; // 20px for padding
			const spaceBelow = window.innerHeight - rect.bottom;
			
			// If there's not enough space below, open upward
			if (spaceBelow < dropdownHeight) {
				shouldOpenUp = true;
			}
		}
		
		setShiftedBy(getShiftedCSS(
			optionHeight,
			shouldOpenUp,
			options,
			selectRef.current,
		));
		setOpen(true);
		setTimeout(() => {
			setActiveClass('open');
		}, 16);

		if (openAtSelected) {
			setTimeout(() => {
				const element = document.getElementById('select-scroll-to');
				if (element) {
					element.scrollTo(0, selectedIndex * optionHeight);
				}
			}, 50);
		}
	}

	useEffect(() => {
		isOpen ? doOpen() : close();
	}, [isOpen]);

	return (
		<div className={classNames(`select-small${enclosed ? ' enclosed' : ''}`, className || '', {
			open,
			invalid: invalid || false,
			disabled,
			title: showLabel,
			'virtuoso-scroller': open,
		}, position)}>
			{showLabel && <label className="select-title bl-font-l2" onClick={doOpen}>{label}</label>}
			<span
				className={classNames(
					'select-label',
					{
						dropdown,
						'has-icon': selected?.icon,
					},
				)}
				onClick={doOpen}
				ref={selectRef}
			>
				<span className={classNames(
					'select-label-text',
					'truncate-overflow',
					'overflow-visible',
					selected.className,
				)}>
					{selected?.icon && (
						<span className="small-select-icon">
							<Icon name={selected.icon} color={COLORS.WHITE} size={selected?.iconSize ?? 13} />
						</span>
					)}
					{selected?.shortLabel ?? selected.label}
					{hasCarrot && (
						<div className={classNames("select-small-carrot", {'is-open-carrot': open})}>
							<Icon name={ICONS.KEYBOARD_ARROW_DOWN} color={COLORS.LIGHT_GRAY} size={12}/>
						</div>
					)}
				</span>
				{dropdown && (
					<Icon
						name={open ? ICONS.KEYBOARD_ARROW_UP : ICONS.KEYBOARD_ARROW_DOWN}
						color={open ? COLORS.WHITE: COLORS.DEFAULT_GRAY}
						size={12}
					/>
				)}
			</span>
			<div className="select-background" onClick={close}></div>
			{open && <div
				onClick={doOpen}
				className={classNames(
					'select-body',
					activeClass,
					dropdownPosition,
					{ 'scroller-border-right-radius': (options?.length ?? 0) * 46 >= 300 },
				)}
				style={{ ...shiftedBy, minWidth }}
				onTransitionEnd={animationFinished}
				id="select-scroll-to"
			>
				{options.map((opt, index) => (
					<React.Fragment key={index}>
						{opt?.icon && (
							<div
								className={classNames('select-small-row', {
									selected: (opt.value === selected.value && activeClass !== 'closed'),
									'selected-no-right-border-radius': (options?.length ?? 0) * 46 >= 300,
								})}
								onClick={(e) => {
									e.stopPropagation();
									onChange(opt.value);
									setSelectedIndex(index);
									close();
								}}
							>
								{opt?.icon && (
									<Icon name={opt.icon} size={opt?.iconSize ?? 12} color={COLORS.WHITE} />
								)}
								<div
									key={opt.value}
										className={classNames(
										'select-option',
										{'has-icon': selected?.icon},
									)}
									style={{ height: optionHeight }}
								>
									<Icon size={12} color={COLORS.WHITE} name={ICONS.CHECK_V2} />
									{opt?.description ? (
										<div className="option-with-description">
											<label className={classNames(opt.className)}>{opt.label}</label>
											<span>{opt.description}</span>
										</div>
									) : <label className={classNames(opt.className)}>{opt.label}</label>}
								</div>
							</div>
						)}
						{!opt?.icon && (
							<div
								key={opt.value}
								onClick={(e) => {
									e.stopPropagation();
									onChange(opt.value);
									setSelectedIndex(index);
									close();
								}}
								className={classNames('select-option', {
									selected: (opt.value === selected.value && activeClass !== 'closed'),
									'selected-no-right-border-radius': (options?.length ?? 0) * 46 >= 300,
								})}
								style={{ height: optionHeight }}
							>
								<Icon size={12} color={COLORS.WHITE} name={ICONS.CHECK_V2} />
								{opt?.description ? (
									<div className="option-with-description">
										<label className={classNames(opt.className)}>{opt.label}</label>
										<span>{opt.description}</span>
									</div>
								) : <label className={classNames(opt.className)}>{opt.label}</label>}
							</div>
						)}
					</React.Fragment>
				))}
			</div>}
			
		</div >
	);
}
